"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { components } from "@/lib/api/schema";
import type { Project } from "@/lib/api/types";
import { insertAt, templateProblems } from "@/lib/briefing-template";
import { PRICE_CHATGPT_EUR, PRICE_GEMINI_EUR, fanoutRunCost, geminiCost } from "@/lib/costs";
import { formatCount, formatDate, formatEuroFine } from "@/lib/format";

type Settings = components["schemas"]["BriefingSettingsOut"];
type Template = components["schemas"]["TemplateOut"];
type Models = components["schemas"]["ModelsOut"];
type TestOut = components["schemas"]["TemplateTestOut"];

const FIELD = "h-11 w-full rounded-md border border-field bg-white px-3 text-sm";
const CARD = "rounded-lg border border-line bg-white";

type Inputs = { model: string; temperature: string; count: string; runsChatgpt: string; runsGemini: string; max: string };

function toInputs(s: Settings): Inputs {
  return {
    model: s.gemini_model ?? "",
    temperature: String(s.temperature).replace(".", ","),
    count: String(s.suggestion_count),
    runsChatgpt: String(s.runs_chatgpt),
    runsGemini: String(s.runs_gemini),
    max: String(s.max_prompts),
  };
}

const num = (text: string) => Number(text.replace(",", "."));
const inRange = (text: string, min: number, max: number, whole: boolean) => {
  const value = num(text);
  return text.trim() !== "" && Number.isFinite(value) && value >= min && value <= max && (!whole || Number.isInteger(value));
};

export function BriefingSettingsPage() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen, damit Eingaben nicht ins andere Projekt wandern
  return project ? <SettingsView key={project.id} project={project} /> : null;
}

type Loaded = { settings: Settings; template: Template; models: Models | null; modelsError: string | null };

function SettingsView({ project }: { project: Project }) {
  const router = useRouter();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const query = `?project_id=${project.id}`;
    Promise.all([
      api<Settings>(`/api/settings/briefing${query}`),
      api<Template>(`/api/settings/briefing/template${query}`),
      // Ohne verbundenes Gemini gibt es keine Modellliste, die Seite funktioniert trotzdem
      api<Models>(`/api/settings/briefing/models${query}`).then(
        (models) => ({ models, modelsError: null }),
        (caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) throw caught;
          return { models: null, modelsError: caught instanceof Error ? caught.message : "Unbekannter Fehler" };
        },
      ),
    ])
      .then(([settings, template, models]) => {
        if (!cancelled) setLoaded({ settings, template, ...models });
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) {
          router.replace(loginUrl(window.location.pathname));
          return;
        }
        setError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
      });
    return () => {
      cancelled = true;
    };
  }, [project.id, router]);

  if (error) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-7 sm:px-9">
        <p role="alert" className="rounded-lg bg-danger-bg px-[18px] py-3.5 text-sm text-danger">
          {error}
        </p>
      </div>
    );
  }
  if (!loaded) return <p className="p-8 text-sm text-muted">Einstellungen werden geladen …</p>;
  return <Form project={project} initial={loaded} />;
}

function Form({ project, initial }: { project: Project; initial: Loaded }) {
  const query = `?project_id=${project.id}`;
  const [settings, setSettings] = useState(initial.settings);
  const [template, setTemplate] = useState(initial.template);
  const [inputs, setInputs] = useState<Inputs>(() => toInputs(initial.settings));
  const [body, setBody] = useState(initial.template.body);
  const [busy, setBusy] = useState<"template" | "step3" | null>(null);
  const [notice, setNotice] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [step3Notice, setStep3Notice] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<{ result?: TestOut; error?: string } | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  const saved = toInputs(settings);
  const problems = templateProblems(body, template.variables);
  const templateDirty = body !== template.body;
  const cardValid = inRange(inputs.temperature, 0, 2, false) && inRange(inputs.count, 3, 20, true);
  const cardDirty = inputs.model !== saved.model || inputs.temperature !== saved.temperature || inputs.count !== saved.count;
  const step3Valid = inRange(inputs.runsChatgpt, 1, 10, true) && inRange(inputs.runsGemini, 1, 10, true) && inRange(inputs.max, 1, 20, true);
  const step3Dirty = inputs.runsChatgpt !== saved.runsChatgpt || inputs.runsGemini !== saved.runsGemini || inputs.max !== saved.max;

  const putSettings = async (patch: Partial<Settings>) => {
    const next = await api<Settings>(`/api/settings/briefing${query}`, {
      method: "PUT",
      json: {
        gemini_model: settings.gemini_model,
        temperature: settings.temperature,
        suggestion_count: settings.suggestion_count,
        runs_chatgpt: settings.runs_chatgpt,
        runs_gemini: settings.runs_gemini,
        max_prompts: settings.max_prompts,
        ...patch,
      },
    });
    setSettings(next);
    return next;
  };

  const save = async () => {
    setBusy("template");
    setNotice(null);
    try {
      const parts: string[] = [];
      if (cardDirty) {
        await putSettings({
          gemini_model: inputs.model || null,
          temperature: num(inputs.temperature),
          suggestion_count: num(inputs.count),
        });
        parts.push("Einstellungen");
      }
      if (templateDirty) {
        const next = await api<Template>(`/api/settings/briefing/template${query}`, {
          method: "POST",
          json: { body },
        });
        setTemplate(next);
        setBody(next.body);
        parts.push(`Vorlage als Version ${next.version}`);
      }
      setNotice({ kind: "ok", text: `Gespeichert: ${parts.join(" und ")}` });
    } catch (caught) {
      setNotice({ kind: "error", text: caught instanceof Error ? caught.message : "Unbekannter Fehler" });
    } finally {
      setBusy(null);
    }
  };

  const saveStep3 = async () => {
    setBusy("step3");
    setStep3Notice(null);
    try {
      await putSettings({ runs_chatgpt: num(inputs.runsChatgpt), runs_gemini: num(inputs.runsGemini), max_prompts: num(inputs.max) });
      setStep3Notice({ kind: "ok", text: "Gespeichert" });
    } catch (caught) {
      setStep3Notice({ kind: "error", text: caught instanceof Error ? caught.message : "Unbekannter Fehler" });
    } finally {
      setBusy(null);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    try {
      const result = await api<TestOut>(`/api/settings/briefing/template/test${query}`, {
        method: "POST",
        json: { body },
      });
      setTest({ result });
    } catch (caught) {
      setTest({ error: caught instanceof Error ? caught.message : "Unbekannter Fehler" });
    } finally {
      setTesting(false);
    }
  };

  const insertVariable = (name: string) => {
    const element = area.current;
    const [next, cursor] = insertAt(body, `{{${name}}}`, element?.selectionStart ?? null, element?.selectionEnd ?? null);
    setBody(next);
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(cursor, cursor);
    });
  };

  const models = initial.models?.models ?? [];
  const defaultModel = initial.models?.default ?? null;
  const knownModel = models.some((m) => m.id === inputs.model);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-col gap-1">
        <div className="text-[13px] text-muted">Einstellungen · Projekt {project.domain}</div>
        <h1 className="text-3xl font-semibold tracking-tight">Briefing</h1>
      </header>

      <div className="flex flex-wrap items-start gap-6">
        <section aria-labelledby="tp-title" className={`${CARD} flex min-w-0 flex-[3_1_560px] flex-col`}>
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-neutral-bg px-5 py-[18px]">
            <div className="flex flex-col gap-0.5">
              <h2 id="tp-title" className="text-base font-semibold">
                Prompt-Vorlage · Schritt 2 „Prompts generieren“
              </h2>
              <span className="text-[13px] text-muted">
                Wird an die Gemini API geschickt. Ziel: herausfinden, was die KI den Nutzer zu diesem Thema fragen würde.
              </span>
            </div>
            <span
              className={`whitespace-nowrap rounded px-2 py-[3px] text-xs font-medium ${
                templateDirty ? "bg-warning-bg text-warning" : "bg-success-bg text-success"
              }`}
            >
              {templateDirty ? "Ungespeicherte Änderungen" : `Version ${template.version} aktiv`}
            </span>
          </div>

          <div className="flex flex-col gap-3.5 px-5 py-[18px]">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="tpl" className="text-[13px] font-medium">
                Vorlage
              </label>
              <textarea
                id="tpl"
                ref={area}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                rows={16}
                spellCheck={false}
                className="w-full resize-y rounded-md border border-field bg-[#fcfcfb] px-3.5 py-3 font-mono text-[13px] leading-relaxed"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">Variablen einfügen</span>
              <div className="flex flex-wrap gap-1.5">
                {template.variables.map((variable) => (
                  <button
                    key={variable.name}
                    type="button"
                    title={variable.description}
                    onClick={() => insertVariable(variable.name)}
                    className="h-11 rounded border border-[#d5dbe4] bg-[#f6f8fb] px-2.5 font-mono text-xs text-[#123a6b]"
                  >
                    {`{{${variable.name}}}`}
                  </button>
                ))}
              </div>
              <span id="var-check" className={`text-xs ${problems.length > 0 ? "text-danger" : "text-muted"}`}>
                {problems.length > 0 ? problems.join(" · ") : "Alle Pflichtvariablen vorhanden."}
              </span>
            </div>

            <div className="flex flex-col gap-1 rounded-md border border-[#e6e9e7] bg-[#f7f8f7] px-3.5 py-3">
              <span className="text-xs font-semibold text-[#3a424a]">Ausgabeformat (fest, wird automatisch angehängt)</span>
              <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-[#4a525b]">{template.output_format}</pre>
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3.5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="model" className="text-[13px] font-medium">
                  Gemini-Modell
                </label>
                <select
                  id="model"
                  value={inputs.model}
                  onChange={(event) => setInputs((c) => ({ ...c, model: event.target.value }))}
                  className={FIELD}
                >
                  <option value="">{defaultModel ? `Standard (${defaultModel})` : "Standard (automatisch)"}</option>
                  {inputs.model !== "" && !knownModel && <option value={inputs.model}>{inputs.model}</option>}
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.label}
                    </option>
                  ))}
                </select>
                {initial.modelsError && <span className="text-xs text-muted">Modellliste nicht verfügbar: {initial.modelsError}</span>}
              </div>
              <NumberField
                id="temperature"
                label="Temperatur"
                value={inputs.temperature}
                valid={inRange(inputs.temperature, 0, 2, false)}
                hint="0 bis 2"
                onChange={(value) => setInputs((c) => ({ ...c, temperature: value }))}
              />
              <NumberField
                id="count"
                label="Anzahl Vorschläge"
                value={inputs.count}
                valid={inRange(inputs.count, 3, 20, true)}
                hint="3 bis 20"
                onChange={(value) => setInputs((c) => ({ ...c, count: value }))}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-b-lg border-t border-neutral-bg bg-[#fafbfa] px-5 py-3.5">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy !== null || problems.length > 0 || !cardValid || (!templateDirty && !cardDirty)}
              aria-describedby="var-check"
              className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white disabled:opacity-60"
            >
              Speichern
            </button>
            <button
              type="button"
              onClick={() => void runTest()}
              disabled={testing || problems.length > 0}
              className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
            >
              {testing ? "Teste …" : "Vorlage testen"}
            </button>
            <span className="text-xs text-muted">Testlauf: {geminiCost}</span>
            {notice && (
              <span role={notice.kind === "error" ? "alert" : "status"} className={`text-[13px] ${notice.kind === "error" ? "text-danger" : "text-success"}`}>
                {notice.text}
              </span>
            )}
            <span className="flex-1" />
            <button type="button" onClick={() => setBody(template.default_body)} className="h-11 px-3.5 text-sm text-danger">
              Auf Standard zurücksetzen
            </button>
          </div>
        </section>

        <aside className="flex min-w-0 flex-[2_1_340px] flex-col gap-5">
          <section aria-labelledby="pv-title" aria-live="polite" className={`${CARD} flex flex-col gap-2.5 px-5 py-[18px]`}>
            <h2 id="pv-title" className="text-base font-semibold">
              Testlauf
            </h2>
            <span className="text-[13px] text-muted">Mit Beispiel „wallbox installieren“ · Ratgeber-Artikel · DE</span>
            {test === null && !testing && (
              <span className="py-3 text-sm text-[#4a525b]">
                Noch kein Testlauf. „Vorlage testen“ schickt die Vorlage einmal an Gemini.
              </span>
            )}
            {testing && <span className="py-3 text-sm text-[#4a525b]">Gemini wird gefragt …</span>}
            {test?.error && (
              <p role="alert" className="rounded-md bg-danger-bg px-3 py-2.5 text-sm text-danger">
                {test.error}
              </p>
            )}
            {test?.result && (
              <>
                <ul>
                  {test.result.prompts.map((p) => (
                    <li key={p.prompt} className="flex flex-col gap-0.5 border-t border-neutral-bg py-2.5">
                      <span className="text-sm leading-snug">{p.prompt}</span>
                      {p.rueckfrage && <span className="text-xs text-muted">Rückfrage: {p.rueckfrage}</span>}
                    </li>
                  ))}
                </ul>
                <span className="text-xs text-success">
                  Gültiges JSON · {formatCount(test.result.aspects)} Aspekte · {formatCount(test.result.prompts.length)} Prompts ·{" "}
                  {test.result.seconds.toLocaleString("de-DE")} s · {formatCount(test.result.tokens)} Tokens
                </span>
              </>
            )}
          </section>

          <section aria-labelledby="vh-title" className={`${CARD} flex flex-col gap-1.5 px-5 py-[18px]`}>
            <h2 id="vh-title" className="mb-1.5 text-base font-semibold">
              Versionen
            </h2>
            <ul>
              {template.versions.slice(0, 6).map((version) => (
                <li key={version.version} className="flex justify-between gap-3 border-t border-neutral-bg py-2 text-[13px]">
                  <span className="font-medium">Version {version.version}</span>
                  <span className="text-muted">
                    {version.created_at ? `${formatDate(version.created_at)} · ${version.label}` : version.label}
                  </span>
                </li>
              ))}
            </ul>
            <span className="text-xs text-muted">Jedes Briefing speichert, mit welcher Version es erstellt wurde.</span>
          </section>

          <section aria-labelledby="fo-title" className={`${CARD} flex flex-col gap-3.5 px-5 py-[18px]`}>
            <h2 id="fo-title" className="text-base font-semibold">
              Schritt 3 „Query-Fan-outs“
            </h2>
            <div className="grid grid-cols-2 gap-3.5">
              <NumberField
                id="runs-chatgpt"
                label="Läufe pro Prompt: ChatGPT"
                value={inputs.runsChatgpt}
                valid={inRange(inputs.runsChatgpt, 1, 10, true)}
                hint={`1 bis 10 · ca. ${formatEuroFine(PRICE_CHATGPT_EUR)} je Lauf`}
                onChange={(value) => setInputs((c) => ({ ...c, runsChatgpt: value }))}
              />
              <NumberField
                id="runs-gemini"
                label="Läufe pro Prompt: Gemini"
                value={inputs.runsGemini}
                valid={inRange(inputs.runsGemini, 1, 10, true)}
                hint={`1 bis 10 · ca. ${formatEuroFine(PRICE_GEMINI_EUR)} je Lauf`}
                onChange={(value) => setInputs((c) => ({ ...c, runsGemini: value }))}
              />
              <NumberField
                id="max"
                label="Max. Prompts"
                value={inputs.max}
                valid={inRange(inputs.max, 1, 20, true)}
                hint="1 bis 20"
                onChange={(value) => setInputs((c) => ({ ...c, max: value }))}
              />
            </div>
            <span className="text-xs leading-normal text-muted">
              Systeme fest: ChatGPT (LLM Scraper) und Gemini (LLM Responses), die Läufe sind je System einstellbar. Ein Gemini-Lauf kostet
              etwa das 15-Fache eines ChatGPT-Laufs, die meisten Kosten sparst du deshalb bei Gemini. Kosten pro Briefing = Prompts ×
              (Läufe ChatGPT × {formatEuroFine(PRICE_CHATGPT_EUR)} + Läufe Gemini × {formatEuroFine(PRICE_GEMINI_EUR)}), je Lauf beider Systeme
              zusammen {fanoutRunCost}.
            </span>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void saveStep3()}
                disabled={busy !== null || !step3Valid || !step3Dirty}
                className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white disabled:opacity-60"
              >
                Speichern
              </button>
              {step3Notice && (
                <span role={step3Notice.kind === "error" ? "alert" : "status"} className={`text-[13px] ${step3Notice.kind === "error" ? "text-danger" : "text-success"}`}>
                  {step3Notice.text}
                </span>
              )}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

type NumberFieldProps = {
  id: string;
  label: string;
  value: string;
  valid: boolean;
  hint: string;
  onChange: (value: string) => void;
};

function NumberField({ id, label, value, valid, hint, onChange }: NumberFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={!valid}
        aria-describedby={`${id}-hint`}
        className={`h-11 w-full rounded-md border bg-white px-3 font-mono text-sm ${valid ? "border-field" : "border-danger"}`}
      />
      <span id={`${id}-hint`} className={`text-xs ${valid ? "text-muted" : "text-danger"}`}>
        {valid ? hint : `Ungültig: ${hint}`}
      </span>
    </div>
  );
}
